// qr_encoder - S47.3b.
// Encoder QR Code "version fija" segun decision del general:
//   - Version: 4 (33x33 modulos)
//   - Error correction: Level L
//   - Mode: Alphanumeric (0..9 A..Z space $%*+-./:)
//   - Mask pattern: 0 (i+j par)
// Capacidad: 78 caracteres alphanumeric Level L. Si el input excede o
// contiene caracteres no alphanumeric, se truncan/uppercase/filtran.
//
// Output: matriz 33x33 de 0/1 lista para dibujar en canvas (1 = modulo
// negro, 0 = modulo blanco).
//
// No incluye QuietZone (4 modulos blancos al rededor); el caller la
// maneja al pintar.
import { rsComputeEC, bchFormat15_5, FORMAT_MASK_15 } from './qr_polynomial';

const N = 33;            // tamano matriz para v4
const DATA_CW = 80;      // data codewords (v4-L)
const EC_CW = 20;        // EC codewords (v4-L)
const TOTAL_BITS = (DATA_CW + EC_CW) * 8;

const ALPHA = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ $%*+-./:';

function _alphaIdx(c) { return ALPHA.indexOf(c); }

function _filterAlpha(s) {
  const upper = String(s == null ? '' : s).toUpperCase();
  let out = '';
  for (let i = 0; i < upper.length; i += 1) {
    if (_alphaIdx(upper[i]) >= 0) out += upper[i];
  }
  // Capacidad maxima v4-L alphanumeric.
  return out.slice(0, 78);
}

// Bit stream helpers.
function _BitBuf() { return { bits: [], len: 0 }; }
function _push(buf, val, n) {
  for (let i = n - 1; i >= 0; i -= 1) {
    buf.bits.push((val >> i) & 1);
    buf.len += 1;
  }
}

function _encodeAlphaBits(s) {
  const buf = _BitBuf();
  _push(buf, 0b0010, 4);     // mode: alphanumeric
  _push(buf, s.length, 9);   // char count v1-v9 alphanumeric = 9 bits
  for (let i = 0; i < s.length; i += 2) {
    if (i + 1 < s.length) {
      const v = _alphaIdx(s[i]) * 45 + _alphaIdx(s[i + 1]);
      _push(buf, v, 11);
    } else {
      _push(buf, _alphaIdx(s[i]), 6);
    }
  }
  // Terminator (hasta 4 bits 0).
  const room = TOTAL_BITS - buf.len;
  const term = Math.min(4, room);
  for (let i = 0; i < term; i += 1) { buf.bits.push(0); buf.len += 1; }
  // Pad a byte boundary.
  while (buf.len % 8 !== 0) { buf.bits.push(0); buf.len += 1; }
  // Codewords.
  const cw = [];
  for (let i = 0; i < buf.bits.length; i += 8) {
    let b = 0;
    for (let j = 0; j < 8; j += 1) b = (b << 1) | buf.bits[i + j];
    cw.push(b);
  }
  // Pad bytes alternantes 0xEC 0x11 hasta 80 codewords.
  let pad = 0;
  while (cw.length < DATA_CW) {
    cw.push(pad === 0 ? 0xEC : 0x11);
    pad = 1 - pad;
  }
  return cw;
}

// Construye matriz NxN inicializada con null (sin valor).
function _newMatrix() {
  const m = new Array(N);
  for (let r = 0; r < N; r += 1) {
    m[r] = new Array(N);
    for (let c = 0; c < N; c += 1) m[r][c] = null;
  }
  return m;
}

function _drawFinder(m, r0, c0) {
  for (let dr = 0; dr < 7; dr += 1) {
    for (let dc = 0; dc < 7; dc += 1) {
      const r = r0 + dr;
      const c = c0 + dc;
      if (r < 0 || r >= N || c < 0 || c >= N) continue;
      const isOuter = dr === 0 || dr === 6 || dc === 0 || dc === 6;
      const isInner = dr >= 2 && dr <= 4 && dc >= 2 && dc <= 4;
      m[r][c] = (isOuter || isInner) ? 1 : 0;
    }
  }
}

function _drawSeparators(m, r0, c0) {
  for (let i = -1; i <= 7; i += 1) {
    if (r0 + i >= 0 && r0 + i < N) {
      if (c0 - 1 >= 0 && c0 - 1 < N) m[r0 + i][c0 - 1] = m[r0 + i][c0 - 1] == null ? 0 : m[r0 + i][c0 - 1];
      if (c0 + 7 >= 0 && c0 + 7 < N) m[r0 + i][c0 + 7] = m[r0 + i][c0 + 7] == null ? 0 : m[r0 + i][c0 + 7];
    }
    if (c0 + i >= 0 && c0 + i < N) {
      if (r0 - 1 >= 0 && r0 - 1 < N) m[r0 - 1][c0 + i] = m[r0 - 1][c0 + i] == null ? 0 : m[r0 - 1][c0 + i];
      if (r0 + 7 >= 0 && r0 + 7 < N) m[r0 + 7][c0 + i] = m[r0 + 7][c0 + i] == null ? 0 : m[r0 + 7][c0 + i];
    }
  }
}

function _drawAlignment(m, r0, c0) {
  for (let dr = -2; dr <= 2; dr += 1) {
    for (let dc = -2; dc <= 2; dc += 1) {
      const r = r0 + dr, c = c0 + dc;
      const isOuter = dr === -2 || dr === 2 || dc === -2 || dc === 2;
      const isCenter = dr === 0 && dc === 0;
      m[r][c] = (isOuter || isCenter) ? 1 : 0;
    }
  }
}

function _drawTiming(m) {
  for (let i = 8; i < N - 8; i += 1) {
    if (m[6][i] == null) m[6][i] = (i % 2 === 0) ? 1 : 0;
    if (m[i][6] == null) m[i][6] = (i % 2 === 0) ? 1 : 0;
  }
}

function _reserveFormat(m) {
  // Top-left: row 8 cols 0..8, col 8 rows 0..7
  for (let i = 0; i <= 8; i += 1) { if (m[8][i] == null) m[8][i] = 0; }
  for (let i = 0; i <= 7; i += 1) { if (m[i][8] == null) m[i][8] = 0; }
  // Top-right: row 8 cols N-8..N-1
  for (let i = 0; i < 8; i += 1) { m[8][N - 1 - i] = 0; }
  // Bottom-left: col 8 rows N-7..N-1
  for (let i = 0; i < 7; i += 1) { m[N - 1 - i][8] = 0; }
  // Dark module (siempre 1) en (4*v + 9, 8) = (25, 8) para v4
  m[N - 8][8] = 1;
}

function _placeFormatBits(m, fmt15) {
  // fmt15 es ya el valor con BCH y XOR aplicados (15 bits).
  // Bit 0 = LSB en mayoria de implementaciones.
  function bit(i) { return (fmt15 >> i) & 1; }
  // Top-left posiciones para bits 0..14:
  // 0..5 -> (8, 0..5); 6 -> (8,7); 7 -> (8,8); 8 -> (7,8); 9..14 -> (5,8)..(0,8)
  const pos = [];
  for (let i = 0; i <= 5; i += 1) pos.push([8, i]);
  pos.push([8, 7]);
  pos.push([8, 8]);
  pos.push([7, 8]);
  for (let i = 5; i >= 0; i -= 1) pos.push([i, 8]);
  for (let i = 0; i < 15; i += 1) m[pos[i][0]][pos[i][1]] = bit(i);
  // Top-right + bottom-left (mismos 15 bits, otro orden):
  // bits 0..7 -> (N-1..N-8, 8) (de abajo hacia arriba)
  // bits 7..14 -> (8, N-8..N-1)
  for (let i = 0; i < 8; i += 1) m[N - 1 - i][8] = bit(i);
  for (let i = 0; i < 7; i += 1) m[8][N - 7 + i] = bit(i + 8);
  // Dark module
  m[N - 8][8] = 1;
}

function _placeDataBits(m, allBits) {
  // Zigzag desde bottom-right; columnas en pares; salta col 6 (timing).
  let bitIdx = 0;
  let upward = true;
  let col = N - 1;
  while (col > 0) {
    if (col === 6) col -= 1;
    for (let i = 0; i < N; i += 1) {
      const r = upward ? (N - 1 - i) : i;
      for (let dc = 0; dc < 2; dc += 1) {
        const c = col - dc;
        if (m[r][c] != null) continue;
        const bit = bitIdx < allBits.length ? allBits[bitIdx] : 0;
        // Mask 0: (r + c) % 2 === 0 -> flip
        const mask = ((r + c) % 2 === 0) ? 1 : 0;
        m[r][c] = bit ^ mask;
        bitIdx += 1;
      }
    }
    upward = !upward;
    col -= 2;
  }
}

// API publica: genera matriz QR 33x33 para `value`.
export function encodeQrV4L(value) {
  const filtered = _filterAlpha(value);
  const dataCw = _encodeAlphaBits(filtered);
  const ecCw = rsComputeEC(dataCw, EC_CW);
  // Para v4-L hay 1 solo bloque, por lo que la secuencia es data + EC.
  const all = dataCw.concat(ecCw);
  // Bits LSB->MSB? No: QR spec dice MSB primero por byte.
  const bits = [];
  for (let i = 0; i < all.length; i += 1) {
    for (let j = 7; j >= 0; j -= 1) bits.push((all[i] >> j) & 1);
  }
  // Padding remainder bits (v4 = 7 remainder bits) -> 0
  while (bits.length < N * N - 0) bits.push(0);

  const m = _newMatrix();
  _drawFinder(m, 0, 0);
  _drawFinder(m, 0, N - 7);
  _drawFinder(m, N - 7, 0);
  _drawSeparators(m, 0, 0);
  _drawSeparators(m, 0, N - 7);
  _drawSeparators(m, N - 7, 0);
  _drawAlignment(m, 26, 26);
  _drawTiming(m);
  _reserveFormat(m);

  _placeDataBits(m, bits);

  // Format info: Level L (01) + mask 0 (000) = 5 bits = 8.
  const fmtRaw = bchFormat15_5(0b01000);
  const fmt15 = fmtRaw ^ FORMAT_MASK_15;
  _placeFormatBits(m, fmt15);

  // Cualquier celda restante null la fijamos a 0 (defensivo).
  for (let r = 0; r < N; r += 1) {
    for (let c = 0; c < N; c += 1) {
      if (m[r][c] == null) m[r][c] = 0;
    }
  }
  return { matrix: m, size: N, value: filtered };
}
